import { cloneDeep } from 'es-toolkit/object'
import { appConfigs } from 'shared/configs/appConfigs'
import {
  DEFAULT_WINDOW_POSITION,
  DEFAULT_WINDOW_SIZE
} from 'shared/constants/apps'
import { logger } from 'shared/services/logger'
import { useCommonStore } from 'shared/stores/commonStore'
import {
  AppInstance,
  BaseWindowState,
  SnapArea,
  WindowState
} from 'shared/types'
import { StateCreator, create } from 'zustand'
import { persist } from 'zustand/middleware'

import { restorableLocalWindows } from './restorableLocalWindows'
import {
  type DesktopSessionV1,
  createDesktopSessionQueryParamStorage,
  migrateDesktopSession,
  rehydrateDesktopSession,
  serializeDesktopSession
} from './windowSessionPersistence'
import { applyWindowSizeToAllWindows } from './windowStore.utils'

interface WindowStoreState {
  windows: WindowState[]
  activeWindowId: string | null
}

interface Snapshot {
  [key: string]: {
    isMaximized: boolean
    isMinimized: boolean
    isVisible: boolean
  }
}

interface WindowStoreActions {
  getWindowState: (id: string) => WindowState | undefined

  openWindow: (
    app: AppInstance & Partial<WindowState>,
    dockIconPosition?: { x: number; y: number }
  ) => void
  closeWindow: (id: string) => void
  closeAllWindows: () => void
  minimizeWindow: (id: string) => void
  minimizeAllWindows: () => () => void
  maximizeWindow: (id: string) => void
  setWindowMaximized: (id: string, isMaximized: boolean) => void

  setActiveWindow: (id: string) => void

  updateWindowPosition: (id: string, position: { x: number; y: number }) => void
  updateWindowSize: (
    id: string,
    size: { width: number; height: number }
  ) => void
  updateAllWindowsSizes: (size: { width: number; height: number }) => void

  updateWindow: (id: string, window: Partial<BaseWindowState>) => void
  setSnapArea: (id: string, snapArea: SnapArea | null) => void

  addToWindowHistory: (windowId: string, route: string) => void
  setCurrentNavigationIndex: (
    windowId: string,
    index: number | ((currentIndex: number) => number)
  ) => void
  removeFromWindowHistory: (windowId: string, length: number) => void
  getWindowLastRoute: (windowId: string) => string | null

  minimizeAllWithSnapshot: () => Snapshot
  restoreFromSnapshot: (snapshot: Snapshot) => void
}

export type WindowStore = WindowStoreState & WindowStoreActions

const initialState: WindowStoreState = {
  windows: [],
  activeWindowId: null
}

const createWindowStore: StateCreator<
  WindowStore,
  [['zustand/persist', unknown]],
  []
> = (set, get) => ({
  ...initialState,

  getWindowState: (id: string) => {
    const { windows } = get()
    return windows.find(w => w.id === id)
  },

  openWindow: (app, dockIconPosition?: { x: number; y: number }) => {
    const { windows } = get()

    const existingWindow = windows.find(w => w.id === app.id)
    if (existingWindow) {
      set({
        activeWindowId: app.id,
        windows: windows.map(w =>
          w.id === app.id ? { ...w, isVisible: true, isMinimized: false } : w
        )
      })
      return
    }

    const topDockHeight = useCommonStore.getState().topDockHeight
    const minY = topDockHeight + 20

    let windowPosition = app.position || null
    if (dockIconPosition) {
      const windowCenterX = dockIconPosition.x - DEFAULT_WINDOW_SIZE.width / 2
      const windowY = minY
      const maxX = globalThis.window.innerWidth - DEFAULT_WINDOW_SIZE.width
      const minX = 0

      windowPosition = {
        x: Math.max(minX, Math.min(windowCenterX, maxX)),
        y: windowY
      }
    } else if (!windowPosition) {
      windowPosition = {
        x: DEFAULT_WINDOW_POSITION.x + windows.length * 30,
        y: Math.max(minY, DEFAULT_WINDOW_POSITION.y + windows.length * 30)
      }
    } else {
      windowPosition = {
        ...windowPosition,
        y: Math.max(minY, windowPosition.y)
      }
    }

    const newWindow: WindowState = {
      id: app.id,
      title: app.label,
      position: windowPosition,
      size: DEFAULT_WINDOW_SIZE,
      canMaximize: app.canMaximize ?? true,
      canMinimize: app.canMinimize ?? true,
      canSnap: app.canSnap ?? true,
      isMinimized: false,
      isMaximized: app.isMaximized ?? false,
      isVisible: true,
      icon: app.icon,
      snapArea: null,
      onCloseCallback: app.onCloseCallback,
      ...(app.url
        ? { url: app.url, currentHistoryIndex: 0, history: [] }
        : {
            localComponent: app.localComponent as NonNullable<
              typeof app.localComponent
            >
          })
    }

    set({
      windows: [...windows, newWindow],
      activeWindowId: app.id
    })
  },

  closeWindow: (id: string) => {
    const { windows, activeWindowId } = get()
    const win = windows.find(w => w.id === id)
    set({
      windows: windows.filter(w => w.id !== id),
      activeWindowId: activeWindowId === id ? null : activeWindowId
    })
    logger.track('window.closed', { windowId: id, appName: win?.title })
  },

  closeAllWindows: () => {
    set({
      windows: [],
      activeWindowId: null
    })
  },

  minimizeWindow: (id: string) => {
    const { windows, activeWindowId } = get()
    set({
      windows: windows.map(w =>
        w.id === id
          ? {
              ...w,
              isMinimized: true,
              isVisible: false
            }
          : w
      ),
      activeWindowId: activeWindowId === id ? null : activeWindowId
    })
    logger.track('window.minimized', { windowId: id })
  },

  minimizeAllWindows: () => {
    const { windows } = get()

    const openWindows = cloneDeep(
      windows.filter(w => w.isVisible && !w.isMinimized)
    )

    set({
      windows: windows.map(w => ({
        ...w,
        isMinimized: true,
        isMaximized: false,
        isVisible: false
      })),
      activeWindowId: null
    })

    const restore = () => {
      const { windows } = get()

      set({
        windows: windows.map(w => {
          const prevState = openWindows.find(w2 => w2.id === w.id)
          return prevState
            ? {
                ...w,
                isMinimized: prevState.isMinimized,
                isMaximized: prevState.isMaximized,
                isVisible: prevState.isVisible
              }
            : w
        }),
        activeWindowId: openWindows[0]?.id ?? null
      })
    }

    return restore
  },

  maximizeWindow: (id: string) => {
    const { windows } = get()
    set({
      windows: windows.map(w =>
        w.id === id ? { ...w, isMaximized: !w.isMaximized } : w
      ),
      activeWindowId: id
    })
    logger.track('window.maximized', { windowId: id })
  },

  setWindowMaximized: (id: string, isMaximized: boolean) => {
    const { windows } = get()
    set({
      windows: windows.map(w =>
        w.id === id
          ? {
              ...w,
              isMaximized,
              isVisible: isMaximized || !w.isMinimized
            }
          : w
      ),
      activeWindowId: id
    })
  },

  setActiveWindow: (id: string) => {
    const { windows } = get()
    set({
      windows: windows.map(w => (w.id === id ? { ...w } : w)),
      activeWindowId: id
    })
  },

  updateWindowPosition: (id: string, position: { x: number; y: number }) => {
    const { windows } = get()
    set({
      windows: windows.map(w => (w.id === id ? { ...w, position } : w))
    })
    logger.track('window.moved', {
      windowId: id,
      x: position.x,
      y: position.y
    })
  },

  updateWindowSize: (id: string, size: { width: number; height: number }) => {
    const { windows } = get()
    set({
      windows: windows.map(w => (w.id === id ? { ...w, size } : w))
    })
    logger.track('window.resized', {
      windowId: id,
      width: size.width,
      height: size.height
    })
  },

  updateAllWindowsSizes: size => {
    const { windows } = get()
    const nextWindows = applyWindowSizeToAllWindows(windows, size)

    if (nextWindows === windows) {
      return
    }

    set({
      windows: nextWindows
    })
  },

  updateWindow: (id: string, window: Partial<BaseWindowState>) => {
    const { windows } = get()
    set({
      windows: windows.map(w => (w.id === id ? { ...w, ...window } : w))
    })
  },

  setSnapArea: (id: string, snapArea: SnapArea | null) => {
    const { windows } = get()
    set({
      windows: windows.map(w => (w.id === id ? { ...w, snapArea } : w))
    })
    if (snapArea) {
      logger.track('window.snapped', { windowId: id, area: snapArea })
    }
  },

  addToWindowHistory: (windowId: string, route: string) => {
    const { windows } = get()
    set({
      windows: windows.map(w =>
        w.id === windowId ? { ...w, history: [...(w.history || []), route] } : w
      ) as WindowState[]
    })
  },

  setCurrentNavigationIndex: (
    windowId: string,
    index: number | ((currentIndex: number) => number)
  ) => {
    const { windows } = get()

    const targetIndex =
      typeof index === 'function'
        ? index(windows.find(w => w.id === windowId)?.currentHistoryIndex || 0)
        : index

    set({
      windows: windows.map(w =>
        w.id === windowId ? { ...w, currentHistoryIndex: targetIndex } : w
      ) as WindowState[]
    })
  },

  removeFromWindowHistory: (windowId: string, length: number) => {
    const { windows } = get()
    set({
      windows: windows.map(w =>
        w.id === windowId ? { ...w, history: w.history?.slice(0, -length) } : w
      ) as WindowState[]
    })
  },

  getWindowLastRoute: (windowId: string) => {
    const { windows } = get()
    const target = windows.find(w => w.id === windowId)

    return target?.history?.length && target.history.length > 0
      ? target.history.at(-1)!
      : null
  },

  minimizeAllWithSnapshot: () => {
    const { windows } = get()
    const snapshot: Snapshot = {}

    for (const w of windows) {
      snapshot[w.id] = {
        isMaximized: !!w.isMaximized,
        isMinimized: !!w.isMinimized,
        isVisible: !!w.isVisible
      }
    }

    set({
      windows: windows.map(w => ({
        ...w,
        isMaximized: false,
        isMinimized: true,
        isVisible: false
      })),
      activeWindowId: null
    })

    return snapshot
  },

  restoreFromSnapshot: snapshot => {
    const { windows } = get()
    set({
      windows: windows.map(w => {
        const prev = snapshot[w.id]
        if (!prev) return w
        return {
          ...w,
          isMaximized: prev.isMaximized,
          isMinimized: prev.isMinimized,
          isVisible: prev.isVisible
        }
      })
    })
  }
})

const desktopSessionStorage = createDesktopSessionQueryParamStorage()

export const useWindowStore = create<WindowStore>()(
  persist<WindowStore, [], [], DesktopSessionV1>(createWindowStore, {
    name: 'desktop',
    storage: desktopSessionStorage,
    version: 1,
    partialize: state => serializeDesktopSession(state),
    migrate: persistedState => migrateDesktopSession(persistedState),
    onRehydrateStorage: () => {
      return () => {
        desktopSessionStorage.markHydrated()
      }
    },
    merge: (persistedState, currentState) => {
      const hydratedState = rehydrateDesktopSession(
        migrateDesktopSession(persistedState),
        {
          iframeWindowsById: Object.fromEntries(
            Object.entries(appConfigs).map(([id, config]) => [
              id,
              {
                title: config.label,
                icon: config.icon
              }
            ])
          ),
          localWindowsById: Object.fromEntries(
            Object.entries(restorableLocalWindows).map(([id, definition]) => [
              id,
              {
                title: definition.title,
                localComponent: definition.localComponent
              }
            ])
          )
        }
      )

      return {
        ...currentState,
        ...hydratedState
      }
    }
  })
)
